import { findNextStep, findPreviousStep, StepInterface } from '@/components/DialogLayoutFullSteps/DialogLayoutFullSteps';
import { Color } from '@/models/Color';
import { PurchaseOrderItem, PurchaseOrderItemKeyInfo } from '@/models/PurchaseOrderItem';
import { Season } from '@/models/Season';
import { Size } from '@/models/Size';
import ErrorHandler from '@/support/ErrorHandler';
import { debounce } from '@fullcalendar/core';
import { AxiosError } from 'axios';
import { Component, Prop, Vue } from 'vue-property-decorator';
import client from "@/support/Client";
import { isArray } from 'lodash';
import { ProductVariant, ProductVariantIncludes } from '@/models/ProductVariant';

@Component<BulkUpdatePurchaseOrderItemsDialog>({})
export default class BulkUpdatePurchaseOrderItemsDialog extends Vue {

  @Prop()
  protected items!: PurchaseOrderItem[];

  protected formDataOrderItem: FormDataOrderItemInterface = {
    quantity: '',
    expectedAt: '',
  };

  protected formDataVariation: FormDataVariationInterface = {
    blockDate: '',
    finalReturnDate: '',
    size: null,
    color: null,
    supplierSize: '',
    supplierColor: '',
    season: null,
    buyPrice: '',
    retailPrice: '',
  };

  protected isSubmitting = false;

  protected PurchaseOrderItemKeyInfo = PurchaseOrderItemKeyInfo;

  protected stepCurrent: BulkUpdatePurchaseOrderItemsDialogStepInterface = {
    label: 'Order item',
    slot: 'step-order-item',
  };

  protected submitResults: undefined | null | ProductVariant[] = undefined;

  protected variationColorItems: Color[] = [];
  protected variationColorSearch = '';
  protected variationColorSearchDebounce = debounce(this.fetchVariationColors, 300);

  protected variationSeasonItems: Season[] = [];
  protected variationSeasonSearch = '';
  protected variationSeasonSearchDebounce = debounce(this.fetchVariationSeasons, 300);

  protected close(): void {
    this.$emit('input', false);
  }

  protected getSubmitPayload(): null | PurchaseOrderItemsBulkUpdateInterface {
    const payload: PurchaseOrderItemsBulkUpdateInterface = {
      with: [
        ProductVariantIncludes.COLOR,
        ProductVariantIncludes.SEASON,
        ProductVariantIncludes.SIZE,
        ProductVariantIncludes.SUPPLIER,
        ProductVariantIncludes.SUPPLIER_SIZE_OBJECT,
      ],
      purchase_order_items: this.items.map((item) => item.id || ''),
      quantity: this.formDataOrderItemChanges.quantity ? this.formDataOrderItem.quantity as number : undefined,
      expected_at: this.formDataOrderItemChanges.expectedAt ? this.formDataOrderItem.expectedAt : undefined,
      block_date: this.formDataVariationChanges.blockDate ? this.formDataVariation.blockDate : undefined,
      final_return_date: this.formDataVariationChanges.finalReturnDate ? this.formDataVariation.finalReturnDate : undefined,
      size: this.formDataVariationChanges.size && this.formDataVariation.size ? this.formDataVariation.size.id : undefined,
      color: this.formDataVariationChanges.color && this.formDataVariation.color ? this.formDataVariation.color.id : undefined,
      season: this.formDataVariationChanges.season && this.formDataVariation.season ? this.formDataVariation.season.id : undefined,
      supplier_size: this.formDataVariationChanges.supplierSize ? this.formDataVariation.supplierSize : undefined,
      supplier_color: this.formDataVariationChanges.supplierColor ? this.formDataVariation.supplierColor : undefined,
      buy_price: this.formDataVariationChanges.buyPrice ? this.formDataVariation.buyPrice : undefined,
      retail_price: this.formDataVariationChanges.retailPrice ? this.formDataVariation.retailPrice : undefined,
    };

    (Object.keys(payload) as (keyof PurchaseOrderItemsBulkUpdateInterface)[]).forEach((key) => {
      if (payload[key] !== undefined) { return; }

      delete payload[key];
    });

    return Object.keys(payload).length
      ? payload
      : null;
  }

  protected onClose(): void {
    this.close();
  }

  protected onButtonCloseClick(): void {
    this.close();
  }

  protected async onButtonNextClick(): Promise<void> {
    const nextStep = findNextStep(this.stepCurrent, this.steps);

    if (! nextStep) { return; }

    this.stepCurrent = nextStep;

    if (this.stepCurrent.slot !== 'step-conclusion') { return; }

    await this.bulkUpdate();
  }

  protected onButtonPreviousClick(): void {
    const previousStep = findPreviousStep(this.stepCurrent, this.steps);

    if (! previousStep) { return; }

    this.stepCurrent = previousStep;
  }

  protected onRowClick(variant: ProductVariant): void {
    const routeData = this.$router.resolve(`/varianten/${variant.id}`);
    window.open(routeData.href, '_blank');
  }

  protected fetchVariationColors(): void {
    new Color()
      .filter("search", this.variationColorSearch)
      .limit(999) // so we can also search inside all the sub-items
      .all()
      .then((colors: Color[]) => {
        let parsedColors: Color[] = [];
        colors.forEach((color: any) => {
          parsedColors.push(color);
          if (color.sub_colors && color.sub_colors.length > 1) {
            color.sub_colors.forEach((subColor: Color) => {
              parsedColors.push(subColor);
            });
          }
        });

        this.variationColorItems = parsedColors;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected fetchVariationSeasons(): void {
    new Season()
      .filter("search", this.variationSeasonSearch)
      .limit(100)
      .all()
      .then((seasonResponse: Season[]) => {
        this.variationSeasonItems = seasonResponse;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected async bulkUpdate(): Promise<void> {
    this.isSubmitting = true;

    const payload = this.getSubmitPayload();

    if (! payload) {
      this.submitResults = null;
      this.isSubmitting = false;
      return;
    }

    try {

      const result: BulkUpdateResultInterface = await client('patch', '/purchase-orders-items/bulk-update', payload);

      this.submitResults = result ? result.data : result;

      this.$emit('updated');
    } catch (error) {
      this.submitResults = null;

      ErrorHandler.network(error);
    }

    this.isSubmitting = false;
  }

  protected get bodyTitle(): string {
    switch (this.stepCurrent.slot) {
      case 'step-order-item':
        return 'Puchase order items';
      case 'step-variation':
        return 'Variation';
      case 'step-summary':
        return 'Summary';
      case 'step-conclusion':
        return 'Result';
      default:
        return '';
    }
  }

  protected get bodySubtitle(): string {
    switch (this.stepCurrent.slot) {
      case 'step-order-item':
        return 'Select the quantity and when the items are to be expected';
      case 'step-variation':
        return 'Update the product variants that are in concept';
      case 'step-summary':
        return 'Validate if the changes below are those that are desired';
      case 'step-conclusion':
        return 'Overview of the results of the bulk update of the order items';
      default:
        return '';
    }
  }

  protected get buttonNextLabel(): string {
    switch (this.stepCurrent.slot) {
      case 'step-summary':
        return 'Update bulk';
      default:
        return 'Next';
    }
  }

  protected get canStepBack(): boolean {
    if (this.stepCurrent.slot === 'step-conclusion') {
      return false;
    }

    return true;
  }

  protected get canStepForward(): boolean {
    return true;
  }

  protected get formDataOrderItemChanges(): FormDataOrderItemChangesInterface {
    return {
      quantity: this.formDataOrderItem.quantity !== '',
      expectedAt: this.formDataOrderItem.expectedAt !== '',
    };
  }

  protected get formDataVariationChanges(): FormDataVariationChangesInterface {
    return {
      blockDate: this.formDataVariation.blockDate !== '',
      finalReturnDate: this.formDataVariation.finalReturnDate !== '',
      size: !! this.formDataVariation.size,
      color: !! this.formDataVariation.color,
      supplierColor: this.formDataVariation.supplierColor !== '',
      supplierSize: this.formDataVariation.supplierSize !== '',
      season: !! this.formDataVariation.season,
      buyPrice: this.formDataVariation.buyPrice !== '',
      retailPrice: this.formDataVariation.retailPrice !== '',
    };
  }

  protected get hasFormDataOrderItemChanges(): boolean {
    return Object.values(this.formDataOrderItemChanges).some((item) => item);
  }

  protected get hasFormDataVariationChanges(): boolean {
    return Object.values(this.formDataVariationChanges).some((item) => item);
  }

  protected get isButtonCloseAvailable(): boolean {
    if (this.stepCurrent.slot === 'step-conclusion') {
      return true;
    }

    return ! findPreviousStep(this.stepCurrent, this.steps);
  }

  protected get isButtonNextAvailable(): boolean {
    if (this.stepCurrent.slot === 'step-summary') {
      return this.hasFormDataOrderItemChanges || this.hasFormDataVariationChanges;
    }

    return !! findNextStep(this.stepCurrent, this.steps);
  }

  protected get isButtonPreviousAvailable(): boolean {
    if (this.stepCurrent.slot === 'step-conclusion') {
      return false;
    }

    return !! findPreviousStep(this.stepCurrent, this.steps);
  }

  protected get isUpdatedSuccessfully(): boolean {
    return this.submitResults !== undefined
      && (
        this.submitResults === null
        || ! isArray(this.submitResults)
        || this.submitResults.length === 0
      );
  }

  protected get isUpdatedPartially(): boolean {
    return this.submitResults !== undefined
      && (
        this.submitResults !== null
        && isArray(this.submitResults)
        && this.submitResults.length > 0
      );
  }

  protected get steps(): BulkUpdatePurchaseOrderItemsDialogStepInterface[] {
    return [
      {
        label: 'Order item',
        slot: 'step-order-item',
      },
      {
        label: 'Variation',
        slot: 'step-variation',
      },
      {
        label: 'Summary',
        slot: 'step-summary',
      },
      {
        label: 'Conclusion',
        slot: 'step-conclusion',
        visible: this.stepCurrent.slot === 'step-conclusion'
      }
    ];
  }
}

interface BulkUpdatePurchaseOrderItemsDialogStepInterface extends StepInterface {
  slot: 'step-order-item' | 'step-variation' | 'step-summary' | 'step-conclusion';
}

interface FormDataOrderItemInterface {
  quantity: string | number;
  expectedAt: string;
};

interface FormDataVariationInterface {
  blockDate: string;
  finalReturnDate: string;
  size: null | Size;
  color: null | Color;
  supplierColor: string;
  supplierSize: string;
  season: null | Season;
  buyPrice: string;
  retailPrice: string;
};

type FormDataOrderItemChangesInterface = Record<keyof FormDataOrderItemInterface, boolean>;

type FormDataVariationChangesInterface = Record<keyof FormDataVariationInterface, boolean>;

interface PurchaseOrderItemsBulkUpdateInterface{
  with: string[];
  purchase_order_items: string[];
  block_date?: string;
  buy_price?: string;
  color?: string;
  expected_at?: string;
  final_return_date?: string;
  quantity?: number;
  retail_price?: string;
  season?: string;
  size?: string;
  supplier_color?: string;
  supplier_size?: string;
}

interface BulkUpdateResultInterface {
  data?: ProductVariant[];
}
